// RECENT ACTIVITY, HOME'S GLANCE (UI rebuild batch 2; round 3 — Astra's Home; depth restored 1 Nov 2026, his "restore
// most the lost things"): the newest twelve transactions on the laptop, five on the phone — what, who, the figure, Dubai
// time — a trade opening its deal, as the old Home's did; then "Money movements →" and "All trades →". A compact panel
// beside the queue on the laptop, a panel under it on the phone (open: it was a closed disclosure).
//
// The entries are Dashboard's journal's (components/MovementTable journalEntries over the movements feed plus the deals it
// does not carry), so a trade is one entry. An AGREED settle-later trade has moved nothing: its figure is in the hint
// colour with "Settle later" beside it, so it never reads as money that moved. The caller owns the reads and their states.
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { SkeletonRows, EmptyState } from '../kit';
import { TradeDetail } from '../trades/TradeDetail';
import { Description, MovementFigure, agreedAccountWords, journalEntries, type MovementLineRow, type StatementLine } from '../../MovementTable';
import type { TradeRow } from '../../../lib/api';
import { dateBucket, displayAsset, fmtAmount, fmtDay, fmtTime } from '../../../lib/format';
import { foldAsset, movementKindLabel, partyName } from '../../../lib/naming';

/** How many transactions the glance names: the old Home's twelve on the laptop, five on the phone. */
const GLANCE = 12;
const GLANCE_PHONE = 5;

/** Astra's plain words for what happened; every other kind keeps its own name (Between parties, Move …). */
const WHAT: Readonly<Record<string, string>> = { trade: 'Trade booked', money_in: 'Money received', money_out: 'Money sent' };

/** A figure that has been AGREED and has not moved (MovementTable's AgreedDelta, copied): the hint colour, never red. */
function AgreedDelta({ value, asset }: { value: number; asset: string }) {
  if (value === 0) return <span className="text-tg-hint">—</span>;
  return (
    <span className="whitespace-nowrap text-tg-hint">
      <span className="amt">{value > 0 ? '+' : '−'}{fmtAmount(Math.abs(value), asset)}</span> {displayAsset(asset)}
    </span>
  );
}

/** A trade's figure, unsigned: the amount and its unit, never split; the hint colour while it is agreed and has not moved. */
function TradeFigure({ value, asset, agreed }: { value: number; asset: string; agreed: boolean }) {
  return (
    <span className={`whitespace-nowrap ${agreed ? 'text-tg-hint' : ''}`}>
      <span className="amt">{fmtAmount(Math.abs(value), asset)}</span> <span className="text-tg-hint">{displayAsset(asset)}</span>
    </span>
  );
}

export function RecentActivity({ rows, dealsById, allPending, quiet, notes, className = '' }: {
  rows: MovementLineRow[];
  dealsById: Map<string, TradeRow>;
  /** Neither read has answered yet. */
  allPending: boolean;
  /** Both reads succeeded and there is nothing: the one empty claim. */
  quiet: boolean;
  /** What could not be read, and what is still being read — said above the rows in hand. */
  notes: ReactNode;
  className?: string;
}) {
  const [deal, setDeal] = useState<TradeRow | null>(null);
  const entries = journalEntries(rows, dealsById).slice(0, GLANCE);
  return (
    <section className={`min-w-0 overflow-hidden rounded-[10px] border border-stroke bg-panel ${className}`}>
      <h2 className="flex min-h-[52px] items-center border-b border-stroke px-4 text-title font-semibold lg:px-5">Recent activity</h2>
      <div>
        {allPending ? (
          <SkeletonRows rows={3} what="recent activity" />
        ) : quiet ? (
          <EmptyState>Nothing booked yet.</EmptyState>
        ) : (
          <>
            {notes && <div className="space-y-1 border-b border-stroke-soft px-4 py-3 lg:px-5">{notes}</div>}
            {entries.length > 0 && (
              <ul>
                {entries.map((e, i) => {
                  const m = e.m;
                  const trade = m.deal_id ? dealsById.get(m.deal_id) ?? null : null;
                  const lead: StatementLine = { key: m.txn_id, m, asset: foldAsset(m.asset), net: null, running: null };
                  const line = e.lines[0];
                  const state = e.agreed ? agreedAccountWords(e.lifecycle) : null;
                  return (
                    <li key={e.key} className={`grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 border-b border-stroke-soft px-4 py-3 lg:px-5 ${i >= GLANCE_PHONE ? 'max-lg:hidden' : ''} ${trade ? 'cursor-pointer hover:bg-panel-2' : ''}`}
                      title={m.notes ? `${m.words}\n${m.notes}` : m.words} onClick={trade ? () => setDeal(trade) : undefined}>
                      {/* ONE FACT PER SEAT (his "one fact per place", 1 Nov 2026): what and who on the left, the time and the figure in
                          their own right-aligned seats — the figures share one numeric edge down the panel. */}
                      <div className="min-w-0">
                        {/* A trade opens its deal (the old Home's Reference did); the row is the tap, this the keyboard's way in. */}
                        {trade
                          ? <button type="button" className="text-left text-body font-semibold hover:underline" onClick={(ev) => { ev.stopPropagation(); setDeal(trade); }}>{WHAT[m.kind] ?? movementKindLabel(m.kind)}</button>
                          : <span className="block text-body font-semibold">{WHAT[m.kind] ?? movementKindLabel(m.kind)}</span>}
                        <div className="mt-1 line-clamp-2 break-words text-meta text-tg-hint">
                          {/* Who: the party; for Between parties, both sides; otherwise the journal's own description. Two lines, never
                              cut mid-name on the first. */}
                          {m.kind === 'p2p' && m.party && m.counterparty
                            ? `${partyName(m.party)} → ${partyName(m.counterparty)}`
                            : m.party ? partyName(m.party) : <Description line={lead} entity="dubaiotc" flow />}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="whitespace-nowrap font-mono tabular text-meta text-tg-hint">{dateBucket(m.at) === 'today' ? fmtTime(m.at) : `${fmtDay(m.at)} ${fmtTime(m.at)}`}</div>
                        {line && (
                          <div className="mt-1 whitespace-nowrap text-body">
                            {/* Signs are for money movements: a trade says its figure plain (in the hint colour while agreed and unmoved). */}
                            {m.kind === 'trade'
                              ? <TradeFigure value={line.net ?? m.amount} asset={line.asset} agreed={e.agreed} />
                              : e.agreed ? <AgreedDelta value={line.net ?? 0} asset={line.asset} /> : <MovementFigure line={line} code />}
                          </div>
                        )}
                        {state && <div className="mt-0.5 text-meta text-tg-hint">{state}</div>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
        <div className="flex flex-wrap gap-x-5 border-t border-stroke-soft px-4 py-1 lg:px-5">
          <Link to="/movements" className="btn-link flex min-h-[44px] items-center text-body lg:min-h-[40px]">Money movements →</Link>
          <Link to="/trades" className="btn-link flex min-h-[44px] items-center text-body lg:min-h-[40px]">All trades →</Link>
        </div>
      </div>
      {deal && <TradeDetail key={deal.deal_id} deal={deal} onClose={() => setDeal(null)} />}
    </section>
  );
}
